import type { ReactNode } from 'react';

export interface RadioCardOption<T extends string> {
  value: T;
  label: string;
  description?: string;
  /** Lands on the `<input type="radio">`, so E2E clicks and `checked` reads hit the real control. */
  testId?: string;
  disabled?: boolean;
  /** A control at the card's right edge, outside the radio's label (Feature Flags: the environment's Enabled switch). */
  end?: ReactNode;
}

interface RadioCardGroupProps<T extends string> {
  /** Shared `name` of the radios: the browser's arrow-key navigation works within it. */
  name: string;
  ariaLabel: string;
  value: T;
  onChange: (value: T) => void;
  options: readonly RadioCardOption<T>[];
  disabled?: boolean;
  className?: string;
}

const COLUMNS = { 1: 'sm:grid-cols-1', 2: 'sm:grid-cols-2', 3: 'sm:grid-cols-3' } as const;

/** The one "pick a mode" control (DESIGN.md §4.4): bordered cards, each a native radio with a label and a description line. */
export function RadioCardGroup<T extends string>({
  name,
  ariaLabel,
  value,
  onChange,
  options,
  disabled = false,
  className = '',
}: RadioCardGroupProps<T>) {
  const columns = COLUMNS[Math.min(options.length, 3) as keyof typeof COLUMNS] ?? COLUMNS[1];

  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className={`grid gap-2 ${columns} ${className}`.trim()}
    >
      {options.map((option) => {
        const off = disabled || option.disabled;
        return (
          <div
            key={option.value}
            className={`flex items-center gap-3 rounded-lg border text-sm ${off ? 'opacity-50' : ''} ${value === option.value ? 'border-brand-500 bg-brand-50 dark:bg-brand-500/10' : 'border-gray-200 dark:border-gray-800'}`}
          >
            <label
              className={`flex flex-1 items-start gap-3 p-3 ${off ? 'cursor-not-allowed' : 'cursor-pointer'}`}
            >
              <input
                type="radio"
                name={name}
                value={option.value}
                checked={value === option.value}
                disabled={off}
                onChange={() => onChange(option.value)}
                className="focus-ring mt-0.5 size-4 accent-brand-500"
                data-testid={option.testId}
              />
              <span>
                <span className="block font-medium text-gray-800 dark:text-gray-200">
                  {option.label}
                </span>
                {option.description && (
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {option.description}
                  </span>
                )}
              </span>
            </label>
            {option.end && <div className="flex pr-3">{option.end}</div>}
          </div>
        );
      })}
    </div>
  );
}
